/**
 * @module AdaptersPage
 * @package @geenius/adapters/react-ant
 * @description Renders the tokenized Ant Design-based adapters overview for the React
 * package. The page summarizes adapter health, supports status filtering, and
 * provides an accessible modal workflow for configuring each adapter domain.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, ADAPTER_STATUSES } from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapters } from "../hooks";

const DIALOG_FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const MODAL_OVERLAY_BACKGROUND =
  "var(--gn-surface-overlay, oklch(0% 0 0 / 40%))";

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the React adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal
 * @property className Optional class name merged onto the page root
 * @property style Optional inline styles merged onto the page root
 * @property registryEntries Optional adapter registry snapshot rendered above the grid
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  className?: string;
  style?: React.CSSProperties;
  registryEntries?: RegistryEntry[];
}

/**
 * Returns focusable descendants for an active dialog container.
 *
 * @param container Dialog container used to scope the focus trap
 * @returns Interactive descendants that should participate in tab order
 */
function getDialogFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTORS),
  );
}

/**
 * Renders the adapter management dashboard for the React variant.
 *
 * @param props Page props controlling navigation overrides and registry data
 * @returns A dashboard with status filters, adapter cards, and a modal editor
 */
export function AdaptersPage({
  onNavigateDetail,
  className,
  style,
  registryEntries,
}: AdaptersPageProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [selectedDomain, setSelectedDomain] = useState<AdapterDomain | null>(
    null,
  );
  const [filterStatus, setFilterStatus] = useState<
    AdapterStatusType | undefined
  >();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalTitleId = useId();
  const { token } = theme.useToken();

  const counts = {
    connected: ADAPTER_DOMAINS.filter(
      (domain) => statuses[domain]?.status === "connected",
    ).length,
    error: ADAPTER_DOMAINS.filter(
      (domain) => statuses[domain]?.status === "error",
    ).length,
    total: ADAPTER_DOMAINS.length,
  };

  useEffect(() => {
    if (!selectedDomain) {
      return;
    }

    const dialog = modalRef.current;
    if (!dialog) {
      return;
    }

    const ownerDocument = dialog.ownerDocument;
    const ownerWindow = ownerDocument.defaultView;
    const previousActiveElement =
      ownerWindow &&
      ownerDocument.activeElement instanceof ownerWindow.HTMLElement
        ? ownerDocument.activeElement
        : null;
    const focusableElements = getDialogFocusableElements(dialog);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements.at(-1);

    (firstFocusableElement ?? dialog).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDomain(null);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const activeElement = ownerDocument.activeElement;

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    ownerDocument.addEventListener("keydown", handleKeyDown);

    return () => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus();
    };
  }, [selectedDomain]);

  if (isLoading) {
    return (
      <div
        className={className}
        style={{
          margin: "0 auto",
          maxWidth: 896,
          display: "flex",
          flexDirection: "column",
          gap: token.marginLG,
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: token.marginMD,
          }}
        >
          <div
            style={{
              height: 32,
              width: 192,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorFillAlter,
            }}
          />
          <div
            style={{
              height: 16,
              width: 288,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorFillQuaternary,
            }}
          />

          <div
            style={{
              display: "grid",
              gap: token.marginSM,
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            }}
          >
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                style={{
                  height: 80,
                  borderRadius: token.borderRadiusLG,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  backgroundColor: token.colorBgContainer,
                }}
              />
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gap: token.marginSM,
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            }}
          >
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                style={{
                  height: 112,
                  borderRadius: token.borderRadiusLG,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  backgroundColor: token.colorBgContainer,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        margin: "0 auto",
        maxWidth: 896,
        display: "flex",
        flexDirection: "column",
        gap: token.marginXXL,
        color: token.colorText,
        ...style,
      }}
    >
      <header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h1
          style={{
            fontSize: token.fontSizeHeading2,
            fontWeight: 800,
            color: token.colorTextHeading,
            margin: 0,
          }}
        >
          Adapters
        </h1>
        <p
          style={{
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            margin: 0,
          }}
        >
          Manage your infrastructure adapters - database, auth, AI, storage,
          payments, and admin.
        </p>
      </header>

      <section
        style={{
          display: "grid",
          gap: token.marginSM,
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        }}
      >
        <div
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorBgContainer,
            padding: token.padding,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            Total
          </p>
          <p
            style={{
              marginTop: 4,
              fontSize: token.fontSizeHeading2,
              fontWeight: 800,
              color: token.colorTextHeading,
              margin: 0,
            }}
          >
            {counts.total}
          </p>
          <p
            style={{
              fontSize: 10,
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            adapter domains
          </p>
        </div>

        <div
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorSuccessBorder}`,
            backgroundColor: token.colorSuccessBg,
            padding: token.padding,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              color: token.colorText,
              margin: 0,
            }}
          >
            Connected
          </p>
          <p
            style={{
              marginTop: 4,
              fontSize: token.fontSizeHeading2,
              fontWeight: 800,
              color: token.colorTextHeading,
              margin: 0,
            }}
          >
            {counts.connected}
          </p>
          <p
            style={{
              fontSize: 10,
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            active adapters
          </p>
        </div>

        {counts.error > 0 && (
          <div
            style={{
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorErrorBorder}`,
              backgroundColor: token.colorErrorBg,
              padding: token.padding,
              boxShadow: token.boxShadowTertiary,
            }}
          >
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                color: token.colorText,
                margin: 0,
              }}
            >
              Errors
            </p>
            <p
              style={{
                marginTop: 4,
                fontSize: token.fontSizeHeading2,
                fontWeight: 800,
                color: token.colorTextHeading,
                margin: 0,
              }}
            >
              {counts.error}
            </p>
            <p
              style={{
                fontSize: 10,
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              need attention
            </p>
          </div>
        )}
      </section>

      {registryEntries && registryEntries.length > 0 && (
        <section
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorBgContainer,
            padding: token.padding,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: token.marginSM,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: token.colorTextSecondary,
                  margin: 0,
                }}
              >
                Adapter Registry
              </p>
              <p
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  margin: 0,
                }}
              >
                Live adapter registry entries.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: token.marginSM,
                fontSize: 12,
                color: token.colorTextSecondary,
              }}
            >
              <span>{registryEntries.length} entries</span>
            </div>
          </div>
          <div
            style={{
              marginTop: token.margin,
              display: "grid",
              gap: token.marginXS,
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            }}
          >
            {registryEntries.map((entry) => (
              <div
                key={`${entry.domain}-${entry.provider}`}
                style={{
                  borderRadius: token.borderRadiusSM,
                  border: `1px solid ${token.colorBorder}`,
                  backgroundColor: token.colorBgLayout,
                  padding: "12px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: token.marginSM,
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: token.fontSizeSM,
                        fontWeight: 600,
                        color: token.colorText,
                        margin: 0,
                      }}
                    >
                      {entry.domain}
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: token.colorTextSecondary,
                        margin: 0,
                      }}
                    >
                      {entry.provider}
                    </p>
                  </div>
                  <AdapterStatusBadge status={entry.status} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={() => setFilterStatus(undefined)}
          aria-pressed={!filterStatus}
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${!filterStatus ? token.colorPrimaryBorder : token.colorBorder}`,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            transition: "all 0.2s",
            backgroundColor: !filterStatus
              ? token.colorPrimaryBg
              : token.colorBgContainer,
            color: !filterStatus ? token.colorText : token.colorTextSecondary,
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            if (filterStatus) e.currentTarget.style.color = token.colorText;
          }}
          onMouseLeave={(e) => {
            if (filterStatus)
              e.currentTarget.style.color = token.colorTextSecondary;
          }}
        >
          All
        </button>
        {ADAPTER_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            aria-pressed={filterStatus === status}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${filterStatus === status ? token.colorPrimaryBorder : token.colorBorder}`,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              transition: "all 0.2s",
              backgroundColor:
                filterStatus === status
                  ? token.colorPrimaryBg
                  : token.colorBgContainer,
              color:
                filterStatus === status
                  ? token.colorText
                  : token.colorTextSecondary,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              if (filterStatus !== status)
                e.currentTarget.style.color = token.colorText;
            }}
            onMouseLeave={(e) => {
              if (filterStatus !== status)
                e.currentTarget.style.color = token.colorTextSecondary;
            }}
          >
            <AdapterStatusBadge status={status} showLabel={false} />
            {status}
          </button>
        ))}
      </section>

      <AdapterList
        filterStatus={filterStatus}
        onSelect={(domain) => {
          if (onNavigateDetail) {
            onNavigateDetail(domain);
          } else {
            setSelectedDomain(domain);
          }
        }}
      />

      {selectedDomain && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: MODAL_OVERLAY_BACKGROUND,
            backdropFilter: "blur(4px)",
            padding: "0 16px",
          }}
        >
          <button
            type="button"
            aria-label="Close adapter configuration"
            style={{
              position: "absolute",
              inset: 0,
              border: 0,
              background: "transparent",
              cursor: "default",
            }}
            onClick={() => setSelectedDomain(null)}
          />
          <div
            ref={modalRef}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 512,
              zIndex: 1,
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            tabIndex={-1}
          >
            <AdapterConfigForm
              domain={selectedDomain}
              titleId={modalTitleId}
              onSave={async () => {
                setSelectedDomain(null);
              }}
              onCancel={() => setSelectedDomain(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
